import { TimelineMax, TweenMax, Linear, Back } from "gsap";
import { Draggable } from "gsap/all";
import Debug from "debug";
import { SessionsCircle } from "./session-circle";

const debug = Debug("cs.MobileSessionCircle");

function isTouchEvent(event: PointerEvent | TouchEvent): event is TouchEvent {
  return (<TouchEvent>event).changedTouches !== undefined;
}

export default class MobileSessionsCircle implements SessionsCircle {
  private circleRadiusInPixels!: number;

  private numberOfSessions!: number;

  private numberOfSessionsForLayout!: number;

  private sessionIds!: string[];

  private circleDragArea!: HTMLElement;

  private circleArcs!: HTMLElement;

  private sessionElements!: HTMLElement[];

  private circleDraggable!: any;

  private onDragEndCallback!: (nearestSessionIndex: number) => void;

  private timeline!: TimelineMax;

  private repeat = false;

  private initialOffsetInDegrees!: number;

  public initialize(
    sessionIds: string[],
    circleRadiusInPixels: number,
    initialOffsetInDegrees: number,
    circleDragArea: HTMLElement,
    circleArcs: HTMLElement,
    sessionElements: HTMLElement[],
    onDragEnd: (nearestSessionIndex: number) => void,
  ): void {
    this.circleRadiusInPixels = circleRadiusInPixels;
    this.initialOffsetInDegrees = initialOffsetInDegrees;
    this.sessionIds = sessionIds;
    this.numberOfSessions = sessionIds.length;
    this.numberOfSessionsForLayout = sessionIds.length <= 6 ? 6 : sessionIds.length;
    this.circleDragArea = circleDragArea;
    this.circleArcs = circleArcs;
    this.sessionElements = sessionElements;
    this.onDragEndCallback = (nearestSessionIndex: number) => {
      onDragEnd(Math.min(this.numberOfSessions - 1, nearestSessionIndex));
    };
    this.initializeCircleAnimation();
  }

  public destroy(): void {
    this.killTweens();
    this.timeline.kill();
    this.circleDraggable.kill();
  }

  public selectSession(sessionIndex: number, durationInSeconds: number): Promise<void> {
    return new Promise((resolve) => {
      // this.selectedSessionIndex = sessionIndex;
      // this.isChanging = true;
      const t = this.repeat
        ? this.timeline.totalTime()
        : Math.min(
            1 + (this.numberOfSessionsForLayout - 1) / this.numberOfSessionsForLayout,
            Math.max(1, this.timeline.totalTime()),
          );
      const currentPositionInIndexForm = (t % 1) * this.numberOfSessionsForLayout;

      // Find the shortest way to get to the selected index, in the current rotation, the next one, or the previous one
      const possibleNewIndexes = this.repeat
        ? [
            sessionIndex,
            sessionIndex + this.numberOfSessionsForLayout,
            sessionIndex - this.numberOfSessionsForLayout,
          ]
        : [sessionIndex];
      const possibleDifferences = possibleNewIndexes.map((i) => i - currentPositionInIndexForm);
      const possibleAbsoluteDistances = possibleDifferences.map((d) => Math.abs(d));
      const closestAbsoluteDistance = Math.min(...possibleAbsoluteDistances);
      const closestIndex =
        possibleNewIndexes[possibleAbsoluteDistances.indexOf(closestAbsoluteDistance)];
      const closestNewPosition = Math.floor(t) + closestIndex / this.numberOfSessionsForLayout;
      const newPosition = closestNewPosition + 0.001;
      debug("selectSession", {
        sessionIndex,
        durationInSeconds,
        t,
        currentPositionInIndexForm,
        newPosition,
      });

      TweenMax.to(this.timeline, durationInSeconds, {
        time: newPosition,
        onComplete: () => {
          debug(`selectSession complete, ${sessionIndex}`);
          resolve();
          // this.isChanging = false;
          // this.newSelectedSessionIndex = this.selectedSessionIndex;
        },
        ease: Back.easeOut.config(1.1),
      });
      TweenMax.set(this.circleDragArea, {
        rotation: this.repeat ? -newPosition * 360 : -(newPosition - 1) * 180,
      });
    });
  }

  private initializeCircleAnimation() {
    const tl = new TimelineMax({ paused: true });
    // const circleRadius = (this.$refs.circle as HTMLElement).clientWidth / 2;
    const initialTimelinePosition = this.repeat ? 100 : 1;
    const arcBezierCurve = this.createArcBezierPoints(
      this.circleRadiusInPixels,
      this.numberOfSessionsForLayout,
    );

    // Reset starting point for rotation
    TweenMax.set(this.circleDragArea, {
      // Start 100 full rotations away from the origin, to avoid messiness with negative time
      rotation: this.repeat ? 100 * 360 : 0,
    });

    this.sessionIds.forEach((sessionId, index) => {
      const startingPosition = 0.5 + index / this.numberOfSessionsForLayout;
      tl.to(
        `[data-session-id=${sessionId}]`,
        1,
        {
          bezier: {
            type: "cubic",
            values: arcBezierCurve,
            autoRotate: false,
          },
          repeat: this.repeat ? -1 : 0,
          ease: Linear.easeNone,
          roundProps: "x,y", // Avoid subpixel graphics glitches with drop-shadow
        },
        startingPosition,
      );
    });

    // Start 100 full rotations away from the origin, to avoid messiness with negative time
    tl.seek(100, true);
    tl.seek(initialTimelinePosition, true);
    this.timeline = tl;

    [this.circleDraggable] = Draggable.create(this.circleDragArea, {
      type: "rotation",
      throwProps: true,
      onDragStart: this.killTweens,
      onDragEnd: this.onDragEnd,
      onDrag: this.onDrag,
      onThrowUpdate: this.onDrag,
      onClick: this.onClick,
    });
  }

  private killTweens = () => {
    TweenMax.killTweensOf([this.circleDragArea, this.timeline]);
  };

  private onDragEnd = () => {
    const totalRotation = this.repeat ? 360 : 180;
    const step = totalRotation / this.numberOfSessionsForLayout;
    const dragRotation = this.repeat
      ? this.circleDraggable.rotation
      : Math.min(
          0,
          Math.max(
            -180 * ((this.numberOfSessionsForLayout - 1) / this.numberOfSessionsForLayout),
            this.circleDraggable.rotation,
          ),
        );
    const nearestIndex =
      (Math.round(-(dragRotation % totalRotation) / step) + this.numberOfSessionsForLayout) %
      this.numberOfSessionsForLayout;
    this.killTweens();
    debug(
      `onDragEnd: ${nearestIndex}, rotation: ${this.circleDraggable.rotation}, correctedRotation: ${dragRotation}`,
    );
    this.onDragEndCallback(nearestIndex);
  };

  private onDrag = () => {
    const currentRotation = this.circleDraggable.rotation;
    const newPosition = this.repeat
      ? 100 +
        ((currentRotation <= 0 ? -currentRotation / 360 : (360 - (currentRotation % 360)) / 360) %
          1)
      : Math.min(2 + 1, Math.max(1 - 1, 1 - currentRotation / 180));
    this.timeline.seek(newPosition, true);
    // debug(`Drag rotation: ${currentRotation}, newPosition: ${newPosition }`);
  };

  private onClick = (event: PointerEvent | TouchEvent) => {
    debug("onClick", event);
    const eventPosition = isTouchEvent(event)
      ? {
          x: event.changedTouches[0].pageX,
          y: event.changedTouches[0].pageY,
        }
      : {
          x: event.pageX,
          y: event.pageY,
        };
    const distancesSquared = this.sessionElements.map((e) => {
      const clientRect = e.getBoundingClientRect() as DOMRect;
      return (
        (clientRect.x + clientRect.width / 2 - eventPosition.x) ** 2 +
        (clientRect.y + clientRect.height / 2 - eventPosition.y) ** 2
      );
    });
    const minimumDistance = Math.min(...distancesSquared);
    const indexOfNearestSession = distancesSquared.indexOf(minimumDistance);
    if (indexOfNearestSession >= 0) {
      this.onDragEndCallback(indexOfNearestSession);
    }
  };

  private createArcBezierPoints(circleRadiusInPixels: number, numberOfSessionsForLayout: number) {
    // https://stackoverflow.com/questions/1734745/how-to-create-circle-with-b%C3%A9zier-curves
    const c = (4 * (Math.sqrt(2) - 1)) / 3;
    const rawOctagonPoints = [
      // Anti-clockwise
      { x: 0, y: -1 }, // 1
      { x: -c, y: -1 }, // 2
      { x: -1, y: -c }, // 3
      { x: -1, y: 0 }, // 4
      { x: -1, y: c }, // 5
      { x: -c, y: 1 }, // 6
      { x: 0, y: 1 }, // 7
    ];

    // Rotate the path so that it starts at the first session's initial position
    const angleOfRotationInRadians = (this.initialOffsetInDegrees / 180) * Math.PI;

    const transformedPoints = rawOctagonPoints.map((point) => {
      return {
        x:
          circleRadiusInPixels *
          (point.x * Math.cos(angleOfRotationInRadians) -
            point.y * Math.sin(angleOfRotationInRadians)),
        y:
          circleRadiusInPixels *
          (point.x * Math.sin(angleOfRotationInRadians) +
            point.y * Math.cos(angleOfRotationInRadians)),
      };
    });
    return transformedPoints;
  }
}
